import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { useDashboardFilters } from "./useDashboardFilters";

type Filters = {
  startDate?: string | null;
  endDate?: string | null;
  businessUnitId?: string | null;
  propertyId?: string | null;
  // add other possible filter keys here
  usagesType?: string | any;
  [key: string]: any;
};

type DashboardFilterContextType = {
  filters: Filters;
  updateFilters: (patch: Partial<Filters>) => void;
  queryParams: Record<string, string>;
  queryString: string;
};

const DashboardFilterContext = createContext<
  DashboardFilterContextType | undefined
>(undefined);

export function DashboardFilterProvider({ children }: { children: ReactNode }) {

  const { filters, updateFilters } = useDashboardFilters();
  // derive query params (only include keys with a truthy value)
  let bodyData = Object?.keys(filters)?.length > 0;
  const queryParams =
    bodyData &&
    useMemo(() => {
      const p: Record<string, string> = {};
      if (filters?.startDate && filters?.startDate !== "all") p.startDate = String(filters.startDate);
      if (filters?.endDate && filters?.endDate !== "all") p.endDate = String(filters.endDate);
      if (filters?.businessUnitId && filters?.businessUnitId !== "all")
        p.businessUnitId = String(filters.businessUnitId);
      if (filters?.usagesType && filters.usagesType !== "all") p.usagesType = filters?.usagesType;
      if (filters?.propertyId && filters?.propertyId !== "all") p.propertyId = String(filters?.propertyId);
      return p;
    }, [filters]);

  const queryString = useMemo(() => {
    const s = new URLSearchParams(queryParams).toString();
    return s ? `?${s}` : "?";
  }, [queryParams]);

  // value is stable unless these dependencies change
  const value = useMemo(
    () => ({ filters, updateFilters, queryParams, queryString }),
    [filters, updateFilters, queryString]
  );

  return <DashboardFilterContext.Provider value={value}>{children}</DashboardFilterContext.Provider>;
}

export function useDashboardFilterContext() {
  const ctx = useContext(DashboardFilterContext);

  return ctx;
}
